// One engine's own end-to-end claim for its full redesign — "here is what is on the
// table" — for surfaces that show the SAVED figures and need the engine's figure
// beside them as a labelled reference.
//
// Why this exists: an engine's per-heuristic rows are steps along one search
// trajectory, not independent deltas. On a real RL run the rows read
// 1273.5 → 769.5 → 303.2 → 300.2 min, each row starting where the previous ended,
// with two rows acting on the same task. Adding those deltas up is only valid if you
// take every one of them, in order, from the start; any subset is meaningless, and
// the percentages (39.6 + 60.6 + 1.0 = 101.2%) never sum at all. So nothing here
// accumulates rows. It reads the engine's single end-to-end figure and nothing else.
//
// Everything comes from `kpi_basis.headline`, which is the backend's own resolution
// of which baseline is honest (the stored process vs. the projected one with
// sub-processes redesigned). Per getHeadlineMetric's contract its percent is a
// compound of effects measured on different bases and must be rendered as sent,
// never recomputed — so when the headline is absent this returns null and the caller
// renders nothing. A missing reference figure is a small loss; a recomputed one
// would be a wrong number in the same row as a right one.
import { getKpiHeadline, getHeadlineMetric } from "./nvaElimination";

// The two engines report cost under different keys AND at different scopes. RL's
// `resource_cost` covers the Responsible role only; BPR's `labor_cost_per_case`
// covers the whole process, the same scope the Save tab's own cost tile uses.
// Printing RL's narrower number under the tile's label would put it on the wrong
// shelf, so the scope travels with the figure and is rendered next to it.
const ENGINE_COST = {
  rl: {
    key: "resource_cost",
    label: "Resource cost",
    scopeNote: "Responsible role only — a narrower measure than the whole-process cost tile, not a projection of it.",
  },
  bpr: {
    key: "labor_cost_per_case",
    label: "Labor cost",
    scopeNote: null,
  },
};

const ENGINE_NAME = { rl: "RL", bpr: "BPR" };

/**
 * @returns {null|{engine,label,time,cost,costLabel,costScopeNote,basis}}
 *   `time`/`cost` are the backend's own {as_is, to_be, reduction_pct} objects, or
 *   null when it did not report that axis.
 */
export function readEngineNativeKpis(result, engine) {
  if (!result) return null;
  const spec = ENGINE_COST[engine];
  if (!spec) return null;

  const headline = getKpiHeadline(result);
  const time = getHeadlineMetric(headline, "cycle_time_minutes");
  const cost = getHeadlineMetric(headline, spec.key);
  if (!time && !cost) return null;

  return {
    engine,
    label: ENGINE_NAME[engine] || engine,
    time,
    cost,
    costLabel: spec.label,
    costScopeNote: spec.scopeNote,
    basis: result?.kpi_basis?.basis || null,
  };
}

/** True when the engine's own claim differs from the stored process at all. */
export function hasNativeMovement(native) {
  const moved = (m) => m && m.as_is != null && m.to_be != null && Math.abs(m.as_is - m.to_be) > 0.005;
  return !!native && (moved(native.time) || moved(native.cost));
}
